import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  Divider,
  Paper
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import RefreshIcon from "@material-ui/icons/Refresh";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TablePagination
} from "@material-ui/core";
import toastError from "../../errors/toastError";
import { listCategories } from "../../services/ticketExtras";
import {
  listCustomCategoryFields,
  createCustomCategoryField,
  updateCustomCategoryField,
  patchActiveCustomCategoryField,
  deleteCustomCategoryField,
} from "../../services/customCategoryFields";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(2), display: 'flex', gap: theme.spacing(2), alignItems: 'flex-start' },
  rootStack: { flexDirection: 'column' },
  sidebar: {
    width: 280,
    flexShrink: 0,
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  },
  section: { display: 'flex', flexDirection: 'column', gap: theme.spacing(1) },
  main: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
  counters: { fontSize: 13, fontWeight: 500 },
  limitsBox: { fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.4 },
  categorySelect: { minWidth: '100%' },
  inactiveRow: { opacity: 0.55 },
  actionsCell: { display: 'flex', gap: 4 },
  tableWrapper: { flex: 1, display: 'flex', flexDirection: 'column' },
  tableCard: { flex: 1, display: 'flex', flexDirection: 'column' },
  tableContainer: { flex: 1, overflow: 'auto' },
  toolbarButtons: { display: 'flex', gap: 8 },
  divider: { margin: theme.spacing(1, 0) }
}));

const FIELD_TYPES = [
  { value: "short_text", label: "Texto Curto" },
  { value: "long_text", label: "Texto Longo" },
  { value: "yes_no", label: "Sim/Não" },
  { value: "checkbox", label: "Checkbox" },
];

const MAX_ACTIVE = 10;
const MAX_TOTAL = 30;

const emptyForm = { name: "", type: "short_text", required: false };

const CustomCategoryFieldsPage = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [fetching, setFetching] = useState(false);

  const activeCount = useMemo(() => fields.filter(f => f.active).length, [fields]);
  const totalCount = fields.length;

  const canCreate = categoryId && totalCount < MAX_TOTAL;

  const validate = useCallback((draft) => {
    const e = {};
    if (!draft.name || !draft.name.trim()) e.name = "Obrigatório";
    if (draft.type === "short_text" && draft.name && draft.name.length > 120) e.name = "Nome muito longo"; // salvaguarda
    if (draft.type === "short_text" && draft.value && draft.value.length > 60) e.value = "Máx 60";
    if (draft.type === "long_text" && draft.value && draft.value.length > 500) e.value = "Máx 500";
    return e;
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await listCategories();
      setCategories(data);
      if (!categoryId && data.length > 0) setCategoryId(data[0].id);
    } catch (err) { toastError(err); }
  }, [categoryId]);

  const loadFields = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await listCustomCategoryFields(id);
      setFields(data);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { if (categoryId) loadFields(categoryId); }, [categoryId, loadFields]);

  // socket updates
  useEffect(() => {
    if (!socket || !user?.companyId) return;
    const channel = `company-${user.companyId}-custom-category-field`;
    const handler = (payload) => {
      if (!payload?.field || payload.field.categoryId !== Number(categoryId)) return;
      setFields(prev => {
        const list = [...prev];
        if (payload.action === "create") {
          list.push(payload.field);
        } else if (payload.action === "update" || payload.action === "patchActive") {
          const idx = list.findIndex(f => f.id === payload.field.id);
          if (idx !== -1) list[idx] = payload.field; else list.push(payload.field);
        } else if (payload.action === "delete") {
          const idx = list.findIndex(f => f.id === payload.field.id);
          if (idx !== -1) list.splice(idx, 1);
        }
        return list.sort((a,b)=> (a.order - b.order) || (a.id - b.id));
      });
    };
    socket.on(channel, handler);
    return () => socket.off(channel, handler);
  }, [socket, user, categoryId]);

  const openNew = () => { setForm(emptyForm); setEditingId(null); setErrors({}); setDialogOpen(true); };
  const openEdit = (row) => { setForm({ name: row.name, type: row.type, required: row.required }); setEditingId(row.id); setErrors({}); setDialogOpen(true); };
  const closeDialog = () => { if (!saving) setDialogOpen(false); };

  const handleSave = async () => {
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      if (editingId) {
        await updateCustomCategoryField(editingId, { name: form.name, type: form.type, required: form.required });
      } else {
        await createCustomCategoryField(categoryId, { name: form.name, type: form.type, required: form.required });
      }
      setDialogOpen(false);
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    if (!row.active && activeCount >= MAX_ACTIVE) { return; }
    try {
      await patchActiveCustomCategoryField(row.id, !row.active);
    } catch (err) { toastError(err); }
  };

  const removeField = async (row) => {
    if (row.usageCount > 0) return;
    if (!window.confirm("Excluir este campo?")) return;
    try { await deleteCustomCategoryField(row.id); } catch (err) { toastError(err); }
  };

  const refresh = async () => { if (categoryId) loadFields(categoryId); };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value,10)); setPage(0); };
  const paginated = useMemo(() => {
    const sorted = [...fields].sort((a,b)=> (a.order - b.order) || (a.id - b.id));
    return sorted.slice(page*rowsPerPage, page*rowsPerPage + rowsPerPage);
  }, [fields, page, rowsPerPage]);

  const theme = useTheme();
  const downMd = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Box className={`${classes.root} ${downMd ? classes.rootStack : ''}`}>
      <Paper className={classes.sidebar} elevation={1}>
        <Typography variant="h6">Campos Personalizados</Typography>
        <div className={classes.section}>
          <FormControl size="small" className={classes.categorySelect} variant="outlined">
            <InputLabel>Categoria</InputLabel>
            <Select label="Categoria" value={categoryId} onChange={(e)=> setCategoryId(e.target.value)}>
              {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <div className={classes.toolbarButtons}>
            <Tooltip title={ !canCreate ? ( totalCount >= MAX_TOTAL ? "Limite total alcançado" : !categoryId ? "Selecione uma categoria" : "" ) : "" }>
              <span style={{ flex: 1 }}>
                <Button startIcon={<AddIcon />} fullWidth color="primary" variant="contained" disabled={!canCreate} onClick={openNew}>Novo</Button>
              </span>
            </Tooltip>
            <Tooltip title="Recarregar">
              <span>
                <IconButton onClick={refresh} disabled={loading}><RefreshIcon /></IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
        <Divider className={classes.divider} />
        <div className={classes.section}>
          <Typography className={classes.counters}>Ativos {activeCount}/{MAX_ACTIVE}</Typography>
          <Typography className={classes.counters}>Total {totalCount}/{MAX_TOTAL}</Typography>
          <div className={classes.limitsBox}>
            <strong>Regras:</strong><br />
            Máx. 10 ativos por categoria.<br />
            Máx. 30 totais (ativos + inativos).<br />
            Tipo não muda após uso.
          </div>
        </div>
      </Paper>
      <div className={classes.main}>
        <Card className={classes.tableCard}>
          <CardContent style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div className={classes.tableContainer}>
          <TableContainer style={{ maxHeight: 560 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Obrigatório</TableCell>
                  <TableCell>Ativo</TableCell>
                  <TableCell>Uso</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={6}>Carregando...</TableCell></TableRow>
                )}
                {!loading && categoryId && paginated.length === 0 && (
                  <TableRow><TableCell colSpan={6}>Nenhum campo</TableCell></TableRow>
                )}
                {!loading && !categoryId && (
                  <TableRow><TableCell colSpan={6}>Selecione uma categoria</TableCell></TableRow>
                )}
                {paginated.map(row => (
                  <TableRow key={row.id} className={!row.active ? classes.inactiveRow : ""} hover>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{FIELD_TYPES.find(t=>t.value===row.type)?.label || row.type}</TableCell>
                    <TableCell>{row.required ? "Sim" : "Não"}</TableCell>
                    <TableCell>
                      <Tooltip title={!row.active && activeCount >= MAX_ACTIVE ? "Limite de 10 ativos" : ""}>
                        <span>
                          <Switch size="small" checked={row.active} onChange={()=>toggleActive(row)} disabled={!row.active && activeCount >= MAX_ACTIVE} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.usageCount}</TableCell>
                    <TableCell align="right">
                      <div className={classes.actionsCell}>
                        <Tooltip title="Editar">
                          <span>
                            <IconButton size="small" onClick={()=>openEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={row.usageCount>0?"Não pode excluir, já usado":"Excluir"}>
                          <span>
                            <IconButton size="small" disabled={row.usageCount>0} onClick={()=>removeField(row)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </div>
          <TablePagination
            component="div"
            rowsPerPageOptions={[25,50,100]}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            count={fields.length}
            labelRowsPerPage="Linhas por página"
          />
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId?"Editar Campo":"Novo Campo"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                label="Nome"
                value={form.name}
                onChange={e=> setForm(prev=> ({...prev, name: e.target.value}))}
                fullWidth
                variant="outlined"
                size="small"
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  label="Tipo"
                  value={form.type}
                  onChange={e=> setForm(prev=> ({...prev, type: e.target.value}))}
                  disabled={!!editingId && fields.find(f=>f.id===editingId)?.usageCount > 0}
                >
                  {FIELD_TYPES.map(t=> <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.required} onChange={e=> setForm(prev=> ({...prev, required: e.target.checked}))} />}
                label="Obrigatório"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomCategoryFieldsPage;
